import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import {
  Heart, Users, Building2, Landmark, HeartHandshake, Camera,
  Mic2, Music, Music2, Lightbulb, Volume2, Mic, Sparkles, ArrowRight, Phone,
} from "lucide-react";
import { useSeo } from "@/lib/seo";

const PRESTATIONS = [
  {
    icon: Heart,
    title: "DJ Mariage",
    desc: "Animation musicale complète pour mariage : cérémonie, vin d'honneur, cocktail, repas, entrée des mariés, ouverture de bal, soirée dansante et moments forts. Ambiance élégante, festive et personnalisée.",
    to: "/mariage",
    img: "https://images.unsplash.com/photo-1482575832494-771f74bf6857?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Users,
    title: "DJ Anniversaire",
    desc: "Animation DJ pour anniversaires, fêtes familiales, soirées privées, départs en retraite et événements personnalisés. Playlist adaptée à l'âge, aux invités et à l'ambiance souhaitée.",
    to: "/anniversaire",
    img: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Building2,
    title: "Entreprises",
    desc: "Prestations pour soirées d'entreprise, séminaires, galas, inaugurations, cocktails professionnels, arbres de Noël, afterworks et événements corporate.",
    to: "/entreprises",
    img: "https://images.unsplash.com/photo-1727096857692-e9dadf2bc92e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkaiUyMGV2ZW50JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzgyODA0NjEzfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Landmark,
    title: "Collectivités & Mairies",
    desc: "Animations pour fêtes municipales, Fête de la Musique, 14 juillet, cérémonies, événements associatifs, inaugurations, soirées publiques et animations locales.",
    to: "/collectivites",
    img: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: HeartHandshake,
    title: "Résidences Seniors",
    desc: "Animations musicales adaptées, conviviales et chaleureuses pour EHPAD, résidences seniors et événements intergénérationnels.",
    to: "/seniors",
    img: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?auto=format&fit=crop&w=1200&q=80",
  },
  {
    icon: Camera,
    title: "Photobooth",
    desc: "Location photobooth avec photos numériques illimitées, impressions selon forfait, thèmes personnalisables et accès en ligne aux photos avec identifiants. Forfaits dès 179 €.",
    to: "/photobooth",
    img: "https://images.pexels.com/photos/9342943/pexels-photo-9342943.jpeg",
  },
  {
    icon: Mic2,
    title: "Blind Test & Quiz",
    desc: "Animation interactive pour bars, entreprises, associations, collectivités, anniversaires et soirées privées.",
    to: "/blind-test-karaoke",
  },
  {
    icon: Music,
    title: "Karaoké",
    desc: "Animation participative idéale pour bars, restaurants, campings, soirées privées, associations et événements d'entreprise.",
    to: "/blind-test-karaoke",
  },
  {
    icon: Music2,
    title: "Chanteur Live & Musiciens",
    desc: "Possibilité d'ajouter un chanteur live, saxophoniste, guitariste ou artistes pour créer une ambiance encore plus forte.",
    to: "/contact",
  },
  {
    icon: Lightbulb,
    title: "Mise en lumière",
    desc: "Mise en lumière élégante de la salle, éclairage de piste de danse, lyres, effets lumineux, éclairage architectural et ambiance visuelle adaptée à l'événement.",
    to: "/contact",
  },
  {
    icon: Volume2,
    title: "Sonorisation",
    desc: "Sonorisation professionnelle adaptée au lieu, à la taille de l'événement et au nombre d'invités.",
    to: "/contact",
  },
  {
    icon: Mic,
    title: "Micro cérémonie & discours",
    desc: "Mise à disposition de micros pour cérémonie laïque, discours, interventions, animations et prises de parole.",
    to: "/contact",
  },
  {
    icon: Sparkles,
    title: "Effets scéniques",
    desc: "Selon l'événement : fumée, geysers, étincelles froides, effets lumineux et moments waouh.",
    to: "/contact",
  },
];

export default function Prestations() {
  useSeo({
    title: "Prestations DJ événementiel | My Animation",
    description:
      "DJ Mariage, anniversaire, entreprise, mairie, photobooth, blind test, karaoké, chanteur live, mise en lumière, sonorisation : découvrez toutes les prestations My Animation dans les Hauts-de-France.",
    path: "/prestations",
    keywords: "prestations DJ événementiel, DJ Hauts-de-France, photobooth Nord, karaoké, blind test",
  });

  return (
    <>
      <PageHero
        eyebrow="Prestations"
        title={<>Une gamme <span className="italic gold-text">complète,</span><br/>un seul interlocuteur.</>}
        subtitle="DJ Mariage, événementiel d'entreprise, mairies, résidences seniors, photobooth, blind test, karaoké, chanteur live, mise en lumière, sonorisation et effets scéniques. My Animation orchestre l'ensemble de vos besoins, de Lille à Tourcoing, Roubaix, Armentières, Dunkerque, Lens, Arras et jusqu'en Belgique."
        img="https://images.unsplash.com/photo-1727096857692-e9dadf2bc92e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkaiUyMGV2ZW50JTIwbGlnaHRpbmd8ZW58MHx8fHwxNzgyODA0NjEzfDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRESTATIONS.map(({ icon: Icon, title, desc, to, img }) => (
            <article
              key={title}
              data-testid={`prestation-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="card-luxe overflow-hidden flex flex-col reveal !p-0"
            >
              {img ? (
                <div className="relative h-44 overflow-hidden">
                  <img src={img} alt={title} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <Icon size={28} strokeWidth={1} className="absolute top-4 right-4 text-[#DCB457]" />
                </div>
              ) : (
                <div className="h-44 bg-[#0a0a0a] flex items-center justify-center border-b border-[#DCB457]/15">
                  <Icon size={42} strokeWidth={0.8} className="text-[#DCB457]" />
                </div>
              )}
              <div className="p-7 md:p-8 flex flex-col flex-1">
                <h3 className="font-serif-luxe text-2xl md:text-3xl">{title}</h3>
                <p className="text-white/60 mt-3 text-sm leading-relaxed flex-1">{desc}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  <Link
                    to="/contact"
                    data-testid={`pres-devis-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="btn-gold !py-2.5 !px-4 !text-[10px]"
                  >
                    Demander un devis
                  </Link>
                  {to && to !== "/contact" && (
                    <Link to={to} className="btn-ghost-gold !py-2.5 !px-4 !text-[10px]">
                      En savoir plus
                    </Link>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-[#DCB457]/15 py-20 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-serif-luxe text-3xl md:text-4xl max-w-xl">
            Une idée précise ? Construisons votre <span className="italic gold-text">devis sur-mesure</span>.
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="prestations-cta-devis" className="btn-gold">
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a href="tel:0769224956" className="btn-ghost-gold">
              <Phone size={14} strokeWidth={1.4} /> Appeler maintenant
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export function CTAFooter() {
  return (
    <section className="border-t border-[#DCB457]/15 py-20 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <h3 className="font-serif-luxe text-3xl md:text-4xl max-w-xl">
          Une idée précise ? Construisons votre <span className="italic gold-text">devis sur-mesure</span>.
        </h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" data-testid="cta-footer-devis" className="btn-gold">
            Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
          <a href="tel:0769224956" className="btn-ghost-gold">
            <Phone size={14} strokeWidth={1.4} /> Appeler maintenant
          </a>
        </div>
      </div>
    </section>
  );
}
